import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { ExpandMore, Person, Business, Security } from '@mui/icons-material';
import { getMe, type MeResponse } from '../../../features/users/api/userApi';
import { useAuthStore } from '../../../features/auth/stores/authStore';
import { Auth } from '@billda/permissions-ui';

export const UserDataSection: React.FC = () => {
  const token = useAuthStore(state => state.token);
  const [userData, setUserData] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get current organization from JWT token
  const getCurrentOrgFromToken = () => {
    if (!token) return null;
    const parsed = Auth.parseToken(token);
    return parsed?.currentOrganisationId ?? null;
  };

  const currentOrgFromToken = getCurrentOrgFromToken();

  // Organize organizations with tagged ones at the top
  const organizeOrganizations = (orgs: any[], databaseInfo: any) => {
    if (!orgs) return [];

    const lastSelectedId = databaseInfo.lastSelectedOrganisation?.id;

    return [...orgs].sort((a, b) => {
      const aIsCurrentFromToken = a.id === currentOrgFromToken;
      const bIsCurrentFromToken = b.id === currentOrgFromToken;
      const aIsLastSelected = a.id === lastSelectedId;
      const bIsLastSelected = b.id === lastSelectedId;

      // Current from token takes priority
      if (aIsCurrentFromToken && !bIsCurrentFromToken) return -1;
      if (!aIsCurrentFromToken && bIsCurrentFromToken) return 1;

      // Then last selected
      if (aIsLastSelected && !bIsLastSelected) return -1;
      if (!aIsLastSelected && bIsLastSelected) return 1;

      // Finally alphabetical by name
      return a.name.localeCompare(b.name);
    });
  };

  // wrap in usecallback to avoid re-creating the function on every render
  const fetchUserData = React.useCallback(async () => {
    if (!token) {
      setError('No authentication token available');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getMe();
      setUserData(response);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to fetch user data'
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchUserData();
    }
  }, [token, fetchUserData]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading user data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{ mb: 2 }}
        action={
          <Button color="inherit" size="small" onClick={fetchUserData}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (!userData) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No user data available
      </Alert>
    );
  }

  const { databaseInfo } = userData;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        User Database Information
      </Typography>

      {/* User Information */}
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Person />
            <Typography variant="subtitle1">User Information</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary">
                Name
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {databaseInfo.givenName} {databaseInfo.familyName}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {databaseInfo.email}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                ID
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {databaseInfo.id}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Phone Numbers ({databaseInfo.phoneNumbers.length})
              </Typography>
              {databaseInfo.phoneNumbers.length > 0 ? (
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ mt: 1 }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Number</TableCell>
                        <TableCell>Is Primary</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {databaseInfo.phoneNumbers.map((phone: any, index) => (
                        <TableRow key={phone.id || index}>
                          <TableCell>
                            {phone.phoneNumber ||
                              phone.number ||
                              phone.value ||
                              `Phone ${index + 1}`}
                          </TableCell>
                          <TableCell>
                            {phone.isPrimary || phone.primary || phone.isDefault
                              ? 'Yes'
                              : 'No'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontStyle: 'italic' }}
                >
                  No phone numbers
                </Typography>
              )}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* All Organizations */}
      {databaseInfo.organisations.length > 0 && (
        <Accordion defaultExpanded sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Business />
              <Typography variant="subtitle1">
                All Organizations ({databaseInfo.organisations.length})
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {organizeOrganizations(
              databaseInfo.organisations,
              databaseInfo
            ).map((org, index) => (
              <Box key={org.id}>
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    bgcolor: 'background.default',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap',
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle2">{org.name}</Typography>
                    {org.id === currentOrgFromToken && (
                      <Chip
                        label="Current (from token)"
                        size="small"
                        color="primary"
                      />
                    )}
                    {org.id === databaseInfo.lastSelectedOrganisation?.id && (
                      <Chip
                        label="Last Selected"
                        size="small"
                        color="secondary"
                      />
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: 'monospace', mb: 1, display: 'block' }}
                  >
                    ID: {org.id}
                  </Typography>

                  {org.roles.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Roles & Permissions
                      </Typography>
                      {org.roles.map((role: any) => (
                        <Box
                          key={role.id}
                          sx={{
                            mb: 1,
                            p: 1,
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <Security fontSize="small" />
                            <Typography variant="body2">{role.name}</Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              ({role.id})
                            </Typography>
                          </Box>

                          <Accordion
                            sx={{
                              boxShadow: 'none',
                              border: 1,
                              borderColor: 'divider',
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMore />}
                              sx={{
                                minHeight: 'auto',
                                '& .MuiAccordionSummary-content': {
                                  margin: '4px 0',
                                },
                              }}
                            >
                              <Typography variant="caption">
                                Permissions ({role.permissions.length})
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                              {role.permissions.length > 0 ? (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 0.5,
                                  }}
                                >
                                  {role.permissions.map((permission: any) => (
                                    <Chip
                                      key={permission.id}
                                      label={permission.name}
                                      size="small"
                                      variant="outlined"
                                      title={`Permission ID: ${permission.id}`}
                                    />
                                  ))}
                                </Box>
                              ) : (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontStyle: 'italic' }}
                                >
                                  No permissions assigned to this role
                                </Typography>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
                {index <
                  organizeOrganizations(
                    databaseInfo.organisations,
                    databaseInfo
                  ).length -
                    1 && <Divider sx={{ my: 2 }} />}
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};
